import {
  CheckOutlined,
  CloseOutlined,
  PaperClipOutlined,
  PlusOutlined
} from "@ant-design/icons"
import { App, Button, DatePicker, Flex, Form, Select, Tooltip } from "antd"
import type { BaseOptionType } from "antd/lib/select"
import React, { useEffect, useMemo, useState } from "react"

import { rowCommitKeyDown } from "../../../../../../../components/EntityTable/editable-row-cells.ts"
import { MODAL_CONFIRM_CONFIG } from "../../../../../../../components/Forms/FormChangedWatcher/FormChangedConfirm.tsx"
import { FormChangedWatcher } from "../../../../../../../components/Forms/FormChangedWatcher/FormChangedWatcher.tsx"
import InputNumber from "../../../../../../../components/Input/InputNumber.tsx"
import { useDateFormat } from "../../../../../../../hooks/use-date-format.ts"
import { dateAutoFill } from "../../../../../../../utils/form/date-auto-fill.tsx"
import { validateFormWithScroll } from "../../../../../../../utils/form/validate-form-with-scroll.tsx"
import { isFormChanged } from "../../../../../../../utils/object/is-form-changed.ts"
import { FINANCE_ITEM_TYPES } from "../../../../constants/finance-item-constants.ts"
import { RESPONSIBLE_TYPES } from "../../../../constants/responsible-type-constants.ts"
import { type ProjectMilestoneFinanceItem } from "../../../../../types/project-phases.types.tsx"
import { ProjectViewFinanceCellDueDate } from "../../../ProjectViewFinanceCellDueDate.tsx"
import { ProjectViewFinancePersonSelect } from "../../ProjectViewFinance/ProjectViewFinance/ProjectViewFinancePersonSelect.tsx"
import {
  buildItemTypeOptions,
  buildResponsibleTypeOptions,
  isExpenseAllowed,
  isPayableAllowed,
  isReceivableAllowed
} from "../../ProjectViewFinance/ProjectViewFinance/responsible-type-options.ts"
import { ResolvedSelect } from "../../../../../../../components/Select/ResolvedSelect.tsx"
import { TextArea } from "../../../../../../../components/Input/TextArea.tsx"

export interface PropsProjectViewMilestoneFinanceItemRowProps {
  record: ProjectMilestoneFinanceItem
  options: Record<string, BaseOptionType[]>
  mode?: "create" | "edit"
  onSelectRow: (
    record: ProjectMilestoneFinanceItem,
    action: string
  ) => Promise<void> | void
  /** May arrive via the table's `onRow`; composed with the row's own handler. */
  onKeyDown?: React.KeyboardEventHandler<HTMLTableRowElement>
}

export const ProjectViewMilestoneFinanceItemRow = ({
  record,
  onSelectRow,
  options,
  mode = "create",
  ...rest
}: PropsProjectViewMilestoneFinanceItemRowProps) => {
  const { getDateValueProps, normalizeDate } = useDateFormat()
  const [saving, setSaving] = useState(false)
  const [form] = Form.useForm<ProjectMilestoneFinanceItem>()
  const { modal } = App.useApp()

  const isCreate = mode === "create"

  const itemType = Form.useWatch("item_type", form)
  const responsibleType = Form.useWatch("responsible_type", form)
  const expenseAllowed = isExpenseAllowed(itemType, record?.is_planned)
  const receivableAllowed = isReceivableAllowed(responsibleType)
  const payableAllowed = isPayableAllowed(responsibleType)
  const responsibleTypeOptions = useMemo(
    () =>
      buildResponsibleTypeOptions(
        options?.["responsible_type"],
        expenseAllowed,
        itemType,
        // No Material here. This row emits a fixed <td> sequence with no
        // material cells, so the option would offer a line nobody can fill in.
        null
      ),
    [options, expenseAllowed, itemType]
  )
  const itemTypeOptions = useMemo(
    () =>
      buildItemTypeOptions(options?.["item_type"], receivableAllowed, payableAllowed),
    [options, receivableAllowed, payableAllowed]
  )

  useEffect(() => {
    if (responsibleType === RESPONSIBLE_TYPES.EXPENSE && !expenseAllowed) {
      form.setFieldValue("responsible_type", null)
    }
  }, [expenseAllowed, responsibleType, form])

  useEffect(() => {
    if (itemType === FINANCE_ITEM_TYPES.RECEIVABLE && !receivableAllowed) {
      form.setFieldValue("item_type", null)
    }
  }, [receivableAllowed, itemType, form])

  useEffect(() => {
    if (itemType === FINANCE_ITEM_TYPES.PAYABLE && !payableAllowed) {
      form.setFieldValue("item_type", null)
    }
  }, [payableAllowed, itemType, form])

  const handleSave = async () => {
    try {
      const { id, ...initialData } = record
      const values = await validateFormWithScroll(form)

      const action = isCreate ? "create" : "update"

      setSaving(true)

      await onSelectRow(
        {
          ...initialData,
          ...values,
          subcontractor: values?.subcontractor || null,
          assignee: values?.assignee || null,
          custom_subcontractor_name: values?.custom_subcontractor_name || null,
          date: normalizeDate(values?.date),
          due_date: normalizeDate(values?.due_date),
          // we don`t need this values
          status: undefined
        },
        action
      )
    } finally {
      setSaving(false)
    }
  }

  const handleCancel = () => {
    const action = isCreate ? "cancel_create" : "cancel"

    const isChanged = isFormChanged<ProjectMilestoneFinanceItem>(
      form.getFieldsValue(),
      record
    )

    if (isChanged) {
      modal.confirm({
        ...MODAL_CONFIRM_CONFIG.cancelChanges,
        onOk() {
          onSelectRow(record, action)
        }
      })
    } else {
      onSelectRow(record, action)
    }
  }

  const onKeyDownRow = rowCommitKeyDown(() => void handleSave(), {
    incoming: rest.onKeyDown,
    busy: saving
  })

  const rowClassName = isCreate ? "bg-blue-50" : ""

  return (
    <Form<ProjectMilestoneFinanceItem>
      form={form}
      component={false}
      initialValues={record}
      disabled={saving}
    >
      <FormChangedWatcher<ProjectMilestoneFinanceItem>
        form={form}
        saving={saving}
      />

      <tr {...rest} onKeyDown={onKeyDownRow} className={rowClassName}>
        {/* Date/Time */}
        <td className="ant-table-cell">
          <Form.Item<ProjectMilestoneFinanceItem>
            name="date"
            noStyle
            getValueProps={getDateValueProps}
            rules={[{ required: true, message: "Required" }]}
          >
            <DatePicker
              className="w-full"
              onChange={dateAutoFill<ProjectMilestoneFinanceItem>({
                form,
                endDatePath: ["due_date"]
              })}
            />
          </Form.Item>
        </td>

        {/* Budget Line Item */}
        <td className="ant-table-cell">
          <Form.Item<ProjectMilestoneFinanceItem>
            name="name"
            noStyle
            rules={[
              { required: true, message: "Enter name" },
              {
                max: 255,
                message: "Must be at least 255 characters"
              }
            ]}
          >
            <TextArea
              autoSize
              placeholder="Enter name"
              autoFocus={isCreate}
            />
          </Form.Item>
        </td>

        {/* Type */}
        <td className="ant-table-cell">
          <Form.Item<ProjectMilestoneFinanceItem>
            name="item_type"
            noStyle
            rules={[{ required: true, message: "Required" }]}
          >
            <Select
              className="w-full"
              placeholder="Select type"
              options={itemTypeOptions}
            />
          </Form.Item>
        </td>

        <ProjectViewFinanceCellDueDate<ProjectMilestoneFinanceItem>
          form={form}
        />

        {/* Comments */}
        <td className="ant-table-cell">
          <Form.Item<ProjectMilestoneFinanceItem>
            name="comment"
            noStyle
            rules={[
              {
                whitespace: true,
                max: 250,
                message: "Must be at least 250 characters"
              }
            ]}
          >
            <TextArea rows={2} autoSize placeholder="Enter comment" />
          </Form.Item>
        </td>

        {/* Value USD */}
        <td className="ant-table-cell">
          <Form.Item<ProjectMilestoneFinanceItem>
            name="value"
            noStyle
            rules={[{ required: false, message: "Required" }]}
          >
            <InputNumber
              min={0}
              max={999999999.99}
              precision={2}
              className="w-full"
              prefix="$"
              placeholder="0.00"
            />
          </Form.Item>
        </td>

        {/* Supplier/Consumer */}
        <td className="ant-table-cell">
          <Form.Item<ProjectMilestoneFinanceItem>
            name="responsible_type"
            noStyle
            normalize={(v) => v || null}
          >
            <Select
              allowClear
              className="w-full"
              placeholder="Select type"
              options={responsibleTypeOptions}
            />
          </Form.Item>
        </td>

        {/* Name */}
        <td className="ant-table-cell">
          <ProjectViewFinancePersonSelect
            form={form}
            subcontractors={options?.["subcontractors"]}
            projectSubcontractors={options?.["project_subcontractors"]}
            teamMembers={options?.["team_members"]}
            suppliers={options?.["suppliers"]}
            hasInitialValue={Boolean(
              record?.custom_subcontractor_name ||
                record?.subcontractor ||
                record?.assignee
            )}
          />
        </td>

        {/* Status */}
        <td className="ant-table-cell">
          <Form.Item<ProjectMilestoneFinanceItem>
            name="status"
            noStyle
            rules={[{ required: false, message: "Required" }]}
          >
            <ResolvedSelect
              disabled
              allowClear
              className="w-full"
              placeholder="Select status"
              options={options?.["status"]}
            />
          </Form.Item>
        </td>

        {/* Attachments */}
        <td className="ant-table-cell text-center">
          <Tooltip title="Only available in view mode">
            <Button disabled icon={<PaperClipOutlined />} />
          </Tooltip>
        </td>

        {/* Actions */}
        <td
          className={`sticky right-0 ant-table-cell ant-table-cell-fix-right ant-table-cell-fix-right-first ${rowClassName}`}
        >
          <Flex gap="small" justify="center">
            <Button
              variant="outlined"
              color="danger"
              onClick={handleCancel}
              icon={<CloseOutlined />}
            />
            <Button
              loading={saving}
              variant="outlined"
              color="green"
              onClick={handleSave}
              icon={isCreate ? <PlusOutlined /> : <CheckOutlined />}
            />
          </Flex>
        </td>
      </tr>
    </Form>
  )
}
